// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useRef, useEffect } from 'react';
import * as yup from 'yup';
import type { StepComponentProps, PolicyTypeKey } from '../../../types/wizard';
import { Dropdown, TextInput } from '@loa/ui-components';
import { toRawNumber, formatCurrencyDisplay, formatEuroBlur } from './currencyHelpers';

const PENSION_TYPE_OPTIONS = [
  { value: '', text: 'Select pension type...' },
  { value: 'PRB_BOB', text: 'PRB / Buy-out Bond' },
  { value: 'PRSA_STANDARD', text: 'PRSA (Standard)' },
  { value: 'PRSA_NON_STANDARD', text: 'PRSA (Non-Standard)' },
  { value: 'PRSA_AVC', text: 'PRSA AVC' },
  { value: 'PERSONAL_PENSION', text: 'Personal Pension' },
  { value: 'DEFINED_CONTRIBUTION', text: 'Defined Contribution' },
  { value: 'EXECUTIVE_PENSION', text: 'Executive Pension' },
  { value: 'MASTERTRUST', text: 'Mastertrust' },
  { value: 'DIRECTORS_PENSION', text: "Director's Pension" },
  { value: 'ARF', text: 'ARF (Approved Retirement Fund)' },
  { value: 'VESTED_PRSA', text: 'Vested PRSA' },
];

const policyDetailsSchema = yup.object({
  policyType: yup.string().required('Pension type is required'),
  fullPolicyNumber: yup.string().trim().required('Policy number is required'),
  currentValue: yup.string().trim().required('Current value is required'),
  currentValueDate: yup.string().required('Date as at current value is required'),
  transferValue: yup.string().trim().required('Transfer value is required'),
  transferValueDate: yup.string().required('Date as at transfer value is required'),
});

function validateField(fieldName: string, data: Record<string, unknown>): string | undefined {
  try {
    policyDetailsSchema.validateSyncAt(fieldName, data);
    return undefined;
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
    return undefined;
  }
}

type StepPolicyDetailsProps = Readonly<StepComponentProps & {
  onPolicyTypeChange?: (policyType: PolicyTypeKey | null) => void;
}>;

export function StepPolicyDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  onPolicyTypeChange,
}: StepPolicyDetailsProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [showPolicyNumberInfo, setShowPolicyNumberInfo] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPolicyNumberInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowPolicyNumberInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPolicyNumberInfo]);

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    // Allow only valid partial numeric input (digits, single dot, decimals)
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) {
      onFieldChange(field, formatEuroBlur(raw));
    }
    handleBlur(field);
  }, [data, onFieldChange, handleBlur]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatCurrencyDisplay(raw) : '';
  };

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Policy details
      </div>

      {/* Row 1: Pension Type* | Policy number* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-sm flex-1 justify-end">
            <Dropdown
              label="Pension Type"
              placeholder="Select pension type..."
              options={PENSION_TYPE_OPTIONS.filter(o => o.value !== '')}
              value={(data.policyType as string) || ''}
              onChange={(val) => {
                onFieldChange('policyType', val);
                onClearFieldError('policyType');
                onPolicyTypeChange?.(val ? (val as PolicyTypeKey) : null);
                handleBlur('policyType');
              }}
              error={getError('policyType')}
              required
            />
          </div>
        </div>

        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-sm flex-1 justify-end">
            <div className="flex items-center justify-between gap-sm">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Policy number / Plan Reference No / Member Reference No <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative shrink-0" ref={infoRef}>
                <button
                  type="button"
                  onClick={() => setShowPolicyNumberInfo((prev) => !prev)}
                  className="cursor-pointer bg-transparent border-none p-0 flex items-center"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="mask_info_policy" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                      <path d="M20 0H0V20H20V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask_info_policy)">
                      <path d="M9.99967 1.04183C14.9413 1.04183 18.958 5.0585 18.958 10.0002C18.958 14.9418 14.9413 18.9585 9.99967 18.9585C5.05801 18.9585 1.04134 14.9418 1.04134 10.0002C1.04134 5.0585 5.05801 1.04183 9.99967 1.04183ZM9.99967 17.7085C14.2497 17.7085 17.708 14.2502 17.708 10.0002C17.708 5.75016 14.2497 2.29183 9.99967 2.29183C5.74967 2.29183 2.29134 5.75016 2.29134 10.0002C2.29134 14.2502 5.74967 17.7085 9.99967 17.7085Z" fill="#141414"/>
                      <path d="M10 8.54183C10.3417 8.54183 10.625 8.82516 10.625 9.16683V13.3335C10.625 13.6752 10.3417 13.9585 10 13.9585C9.65833 13.9585 9.375 13.6752 9.375 13.3335V9.16683C9.375 8.82516 9.65833 8.54183 10 8.54183Z" fill="#141414"/>
                      <path d="M9.99967 5.83262C10.108 5.83262 10.2163 5.85762 10.3163 5.89928C10.4163 5.94095 10.508 5.99928 10.5913 6.07428C10.6663 6.15762 10.7247 6.24095 10.7663 6.34928C10.808 6.44928 10.833 6.55762 10.833 6.66595C10.833 6.77428 10.808 6.88262 10.7663 6.98262C10.7247 7.08262 10.6663 7.17428 10.5913 7.25762C10.508 7.33262 10.4163 7.39095 10.3163 7.43262C10.1163 7.51595 9.88301 7.51595 9.68301 7.43262C9.58301 7.39095 9.49134 7.33262 9.40801 7.25762C9.33301 7.17428 9.27467 7.08262 9.23301 6.98262C9.19134 6.88262 9.16634 6.77428 9.16634 6.66595C9.16634 6.55762 9.19134 6.44928 9.23301 6.34928C9.27467 6.24095 9.33301 6.15762 9.40801 6.07428C9.49134 5.99928 9.58301 5.94095 9.68301 5.89928C9.78301 5.85762 9.89134 5.83262 9.99967 5.83262Z" fill="#141414"/>
                    </g>
                  </svg>
                </button>
                {showPolicyNumberInfo && (
                  <div className="absolute right-0 top-[28px] z-10 w-[280px] px-[12px] py-sm rounded-lg bg-black text-white font-sans text-[13px] font-normal leading-normal shadow-lg">
                    This number may be referred to by various names depending on the provider
                  </div>
                )}
              </div>
            </div>
            <TextInput
              value={(data.fullPolicyNumber as string) || ''}
              onChange={(e) => {
                onFieldChange('fullPolicyNumber', e.target.value);
                onClearFieldError('fullPolicyNumber');
              }}
              onBlur={() => handleBlur('fullPolicyNumber')}
              error={getError('fullPolicyNumber')}
              fluid
            />
          </div>
        </div>
      </div>

      {/* Row 3: Current value* | Date as at current value* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Current value <span className="text-[#E7000B]">*</span>
          </div>
          <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('currentValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('currentValue')}
              onChange={(e) => handleCurrencyChange('currentValue', e.target.value)}
              onBlur={() => handleCurrencyBlur('currentValue')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('currentValue') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('currentValue')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date as at current value <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.currentValueDate as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('currentValueDate');
              }}
              onChange={(e) => {
                onFieldChange('currentValueDate', e.target.value);
                onClearFieldError('currentValueDate');
              }}
              className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('currentValueDate') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.currentValueDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
            />
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask_cvd" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <path d="M16 0H0V16H16V0Z" fill="white"/>
              </mask>
              <g mask="url(#mask_cvd)">
                <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
                <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
                <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
                <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
              </g>
            </svg>
          </div>
          {getError('currentValueDate') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('currentValueDate')}</div>
          )}
        </div>
      </div>

      {/* Row 4: Transfer value* | Date as at transfer value* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Transfer value <span className="text-[#E7000B]">*</span>
          </div>
          <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('transferValue') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('transferValue')}
              onChange={(e) => handleCurrencyChange('transferValue', e.target.value)}
              onBlur={() => handleCurrencyBlur('transferValue')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('transferValue') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transferValue')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date as at transfer value <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.transferValueDate as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('transferValueDate');
              }}
              onChange={(e) => {
                onFieldChange('transferValueDate', e.target.value);
                onClearFieldError('transferValueDate');
              }}
              className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('transferValueDate') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.transferValueDate as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
            />
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask_tvd" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <path d="M16 0H0V16H16V0Z" fill="white"/>
              </mask>
              <g mask="url(#mask_tvd)">
                <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
                <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
                <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
                <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
              </g>
            </svg>
          </div>
          {getError('transferValueDate') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transferValueDate')}</div>
          )}
        </div>
      </div>

      {/* Row 5: Scheme Revenue Reference Number */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <TextInput
            label="Scheme Revenue Reference Number"
            value={(data.schemeRevenueRefNumber as string) || ''}
            onChange={(e) => {
              onFieldChange('schemeRevenueRefNumber', e.target.value);
              onClearFieldError('schemeRevenueRefNumber');
            }}
            onBlur={() => handleBlur('schemeRevenueRefNumber')}
            helperText="eg. SF123456 or sometimes PB123456"
            error={getError('schemeRevenueRefNumber')}
            fluid
          />
        </div>
        <div className="flex-1" />
      </div>
    </div>
  );
}
